import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { useDispatch, useSelector } from 'react-redux';
import { throttleRAF } from '../../../utils/throttle';
import {
    resetOperation, setOperationData,
    setCommandMessage,
} from '../../../redux/operationSlice';
import { createSnapMarker } from '../../../shared/snap/createSnapMarker';

import { getSnappedPoint } from '../../../shared/snap/getSnappedPoint';
import { createGroundingStrake } from '../../../factory/symbols/createGroundingStrake';
import { createTwoLineLabel } from '../../../factory/symbols/createGroundingStrake';
import { getPerpendicularIntersection, isAxisAligned, getPreviewRayIntersection, getLineDirection, getDirectionalIntersection } from '../../../shared/geometry/getPrependicularIntersection';
import { applyGridSnap, getOrthoSnappedPoint } from "../../../modules/drawing";
import useDraggableLabels from './useGroundingDraggableLabels';



const useMPanelGroundWiring = (scene, camera, renderer, snapPoints, options = {}) => {
    useDraggableLabels(scene, camera, renderer);
    const {
        enabled = () => { },
        onWireEnd = () => { },
        onWireStart = () => { },
        onSocketPlace = () => { },
        onWireCancel = () => { },
    } = options;

    const strakeCount = useRef(0);
    const strakeLength = useRef(0);
    const groundingArea = useRef([]);
    const startRef = useRef(false);
    const verticesRef = useRef([]);
    const tempLineRef = useRef(null);
    const finalPointRef = useRef(null);
    const snapMarkerRef = useRef(null);
    const snapSourceRef = useRef(null);
    const previewSymbolRef = useRef(null);
    const previewMarkersRef = useRef([]); // [{ startIdx, line }]
    const offsetLineRef = useRef(null); // SnapWall kopya çizgisi
    const intersectedLineRef = useRef(null); // Mavi olacak çizgi
    const originalMainPanelWirePositions = useRef(null);
    const lastMouse = useRef({ x: 0, y: 0 });
    const previewEndPointRef = useRef({ x: 0, y: 0 });
    const socketCountRef = useRef(0);
    const socketTypes = ['normal'];
    const socketTypeIndex = useRef(0);
    const socketTypeRef = useRef('normal');
    const socketsRef = useRef([])
    const flipDirectionRef = useRef(0); // 0, 90, 180, 270 derece değerleri
    const mainPanelWireOffsetApplied = useRef(false);
    const lastOffsetDirectionRef = useRef(null); // ✅ Son açılı offset çizgisinin yönü
       
    const [flipDir, setFlipDir] = useState(0);
    const [step, setStep] = useState(0); // 0: ilk nokta, 1+: devam

    const { commandType } = useSelector((state) => state.operation);
    const gridMode = useSelector((state) => state.mods.gridMode);
    const objectSnap = useSelector((state) => state.mods.objectSnap);
    const operationData = useSelector((state) => state.operation.data);
    const snapMode = useSelector((state) => state.mods.snapMode);
    const orthoMode = useSelector((state) => state.mods.orthoMode); // Ortho modu Redux'tan

    const snapWall = true;
    const wireColor = 0x0045ff
     const snapState = {
        x: 0, // 0: İlk adım, 1: Tam hizalama, 2: Üçüncü adım (5 birim kayma)
        y: 0, // Aynı şekilde Y için de
    };
    const setFlipDirection = (valOrUpdater) => {
        const newValue = typeof valOrUpdater === 'function' ? valOrUpdater(flipDirectionRef.current) : valOrUpdater;
        flipDirectionRef.current = newValue;
        setFlipDir(newValue);
    };

    const dispatch = useDispatch();

    // YENİ: Köşeye yakınlığı kontrol eden fonksiyon (istekte belirtildiği gibi).
    const findClosingVertexIndex = (point, vertices, threshold = 10) => {
        // Son köşe hariç diğerlerini kontrol et, kendine kapanmasın.
        for (let i = 0; i < vertices.length - 1; i++) {
            if (point.distanceTo(vertices[i]) <= threshold) {
                return i; // Yakın olan köşenin index'ini döndür
            }
        }
        return -1; // Yakın köşe bulunamadı
    };

    useEffect(() => {
        if (
            step === 0 &&
            operationData?.initialPoint &&
            enabled() &&
            verticesRef.current.length === 0
        ) {
            const startPoint = new THREE.Vector3(
                operationData.initialPoint.x,
                operationData.initialPoint.y,
                0
            );

            // MainPanel'i bul ve giriş hattı ile paralel yönü hesapla
            let parallelPoint = null;
            scene.traverse(obj => {
                if (obj.userData?.type === 'mainPanel') {
                    const panelRotation = obj.rotation.z;

                    // MainPanel'e giren hat yönü ile paralel (aynı direction)
                    const parallelDirection = new THREE.Vector3(
                        -Math.cos(panelRotation), // Giriş hattı yönü
                        -Math.sin(panelRotation),
                        0
                    );

                    // 10 birim uzunlukta paralel kenar
                    parallelPoint = startPoint.clone().add(parallelDirection.multiplyScalar(10));
                }
            });

            // İlk nokta (başlangıç - MainPanel'in 7.5 birim arkası)
            verticesRef.current.push(startPoint);
            startRef.current = true;

            // İkinci nokta (10 birim paralel kenar sonu)
            if (parallelPoint) {
                verticesRef.current.push(parallelPoint);

                // İlk paralel kenar çizgisini hemen oluştur
                const geometry = new THREE.BufferGeometry().setFromPoints([startPoint, parallelPoint]);
                const material = new THREE.LineBasicMaterial({ color: 0x00ff00 }); // Yeşil renk
                const line = new THREE.Line(geometry, material);

                line.userData = {
                    type: 'groundingWire',
                    isGroundingLine: true
                };

                scene.add(line);
            }

            setStep(2); // 2 nokta zaten var, 3. noktayı bekle
        }
    }, [operationData, step, enabled, scene]);

    useEffect(() => {
        if (!enabled() || !scene || !camera || !renderer) return;

        const domElement = renderer.domElement;

        // YENİ: Kod tekrarını önlemek için 'Enter' tuşu ve otomatik kapanma mantığını içeren fonksiyon
        const completeWiring = () => {
            setStep(0);
            if (!socketsRef.current.length && verticesRef.current.length > 1) {
                // Son noktanın direction'ını hesapla
                const p1 = verticesRef.current.at(-2);
                const p2 = verticesRef.current.at(-1);
                const direction = new THREE.Vector3().subVectors(p2, p1);
                const angle = Math.atan2(direction.y, direction.x);

                // Yeni soket verisi
                const socketData = {
                    position: p2.clone(),
                    type: socketTypeRef.current,
                    buat: null,
                    direction,
                };
                socketsRef.current.push(socketData);

                // Topraklama kazığı (strake) oluşturuluyor
                const socket = createGroundingStrake(
                    scene,
                    p2.clone(),
                    1, // direction için sabit
                    wireColor,
                    false, // preview değil, gerçek
                    socketsRef.current,
                    tempLineRef.current?.uuid,
                    90 + flipDir // 90 derece + r tuşu ile eklenen rotasyon (state kullan)
                );
                socket.rotation.z = angle; // Direction'ı doğru uygula

                // UUID kaydet
                const uuid = socket.uuid;
                socketsRef.current[socketsRef.current.length - 1].uuid = uuid;
                strakeCount.current++
                scene.add(socket);
                socketCountRef.current++;
            }
            let area = 0;
            let totalLength = 0;
            if (verticesRef.current.length > 1) {
                for (let i = 0; i < verticesRef.current.length - 1; i++) {
                    totalLength += verticesRef.current[i].distanceTo(verticesRef.current[i + 1]);
                    const j = (i + 1) % verticesRef.current.length; // Son nokta ilk noktaya bağlanır
                    area += verticesRef.current[i].x * verticesRef.current[j].y;
                    area -= verticesRef.current[j].x * verticesRef.current[i].y;
                }
                strakeLength.current += totalLength / 100
                area = Math.abs(area) / 20000;
                groundingArea.current.push(area)

                // Kalıcı çizgiyi oluştur
                const geometry = new THREE.BufferGeometry().setFromPoints(verticesRef.current);
                const material = new THREE.LineBasicMaterial({ color: wireColor }); // Kalıcı çizgi rengi
                const permanentLine = new THREE.Line(geometry, material);

                permanentLine.uuid = tempLineRef.current?.uuid ?? THREE.MathUtils.generateUUID();
                permanentLine.userData = {
                    type: 'groundingStrip',
                    sockets: socketsRef.current.map(s => s.uuid),
                };

                scene.add(permanentLine);

                // onWireEnd'e bildir
                onWireEnd(verticesRef.current, socketsRef.current, snapSourceRef.current, permanentLine.uuid);
            }

            if (previewSymbolRef.current) {
                scene.remove(previewSymbolRef.current);
                previewSymbolRef.current.traverse((child) => {
                    if (child.geometry) child.geometry.dispose();
                    if (child.material) child.material.dispose();
                });
                previewSymbolRef.current = null;
            }

            if (tempLineRef.current) {
                scene.remove(tempLineRef.current);
                tempLineRef.current.geometry.dispose();
                tempLineRef.current.material.dispose();
                tempLineRef.current = null;
            }

            if (snapMarkerRef.current) {
                scene.remove(snapMarkerRef.current);
                snapMarkerRef.current.geometry.dispose();
                snapMarkerRef.current.material.dispose();
                snapMarkerRef.current = null;
            }

            socketsRef.current = [];
            verticesRef.current = [];
            startRef.current = false;
            snapSourceRef.current = null;


            setStep(-1);
        };


        const handleClick = (event) => {
            if (!enabled()) return;

            const rect = domElement.getBoundingClientRect();
            const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
            const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
            lastMouse.current = { x, y };

            const mouseNDC = new THREE.Vector2(x, y);
            const worldPoint = new THREE.Vector3(x, y, 0).unproject(camera);

            let dynamicSnapPoints = [...snapPoints];

            let finalPoint = worldPoint;
            let snapped = false;
            let snapSource = null;

            if (objectSnap && snapMode) {
                ({ finalPoint, snapped, snapSource } = getSnappedPoint(mouseNDC, worldPoint, dynamicSnapPoints, camera, renderer, { snapMode }));
            }

            if (!startRef.current && step === 0) {
                startRef.current = true;

                //----------------------
                const buat = new THREE.Mesh(
                    new THREE.CircleGeometry(4, 32),
                    new THREE.MeshBasicMaterial({ color: 0x0045ff, side: THREE.DoubleSide })
                );
                buat.position.set(finalPoint.x, finalPoint.y, 0.2);
                buat.userData = {
                    type: 'buatCircle',
                    socketUUID: "uuid",
                    isDraggable: false,
                };
                const textSprite = createTwoLineLabel('Bağlantı Filizi', '');
                textSprite.position.set(finalPoint.x + 0, finalPoint.y + 0, 0.3); // Başlangıç offset
                textSprite.userData = {
                    type: 'groundingBuatLabel',
                    parentBuat: buat,
                    offset: new THREE.Vector3(10, 30, 0),
                };
                scene.add(textSprite);
                //-----------------------------

                setStep(1);
            }

            if (!snapped) {
                if (gridMode) {
                    finalPoint = applyGridSnap(worldPoint);
                } else {
                    finalPoint = worldPoint;
                }
            }
            if (snapWall && offsetLineRef.current && verticesRef.current.length > 0) {
                const targetLine = offsetLineRef.current;
                const positions = targetLine.geometry.attributes.position.array;
                const start = new THREE.Vector3(positions[0], positions[1], positions[2]);
                const end = new THREE.Vector3(positions[3], positions[4], positions[5]);
                const lastVertex = verticesRef.current[verticesRef.current.length - 1];
                
                // Çizgi eksen hizalı mı kontrol et
                if (isAxisAligned(start, end)) {
                    // Eksen hizalı çizgiler için
                    if (lastOffsetDirectionRef.current) {
                        // Önceki açılı çizginin yönünde devam et
                        finalPoint = getDirectionalIntersection(lastVertex, lastOffsetDirectionRef.current, { start, end });
                    } else {
                        // Normal perpendicular intersection kullan
                        finalPoint = getPerpendicularIntersection(lastVertex, { start, end });
                    }
                    // Eksen hizalı çizgiye tıklandığında açılı yön sıfırla
                    lastOffsetDirectionRef.current = null;
                } else {
                    // Açılı çizgiler için
                    if (lastOffsetDirectionRef.current) {
                        // Önceki açılı çizginin yönünde devam et
                        finalPoint = getDirectionalIntersection(lastVertex, lastOffsetDirectionRef.current, { start, end });
                    } else {
                        // Mouse yönünde kesişim hesapla
                        finalPoint = getPreviewRayIntersection(lastVertex, worldPoint, { start, end });
                    }
                    // Açılı çizginin yönünü sakla (sonraki çizim için)
                    lastOffsetDirectionRef.current = getLineDirection(start, end);
                }
            } else if (!snapped) {
                finalPoint = gridMode ? applyGridSnap(worldPoint) : worldPoint;
            }

            if (orthoMode && verticesRef.current.length > 0) {
                const lastVertex = verticesRef.current[verticesRef.current.length - 1];
                finalPoint = getOrthoSnappedPoint(lastVertex, finalPoint);
            }

            // YENİ: Otomatik Kapanma Kontrolü
            // En az 2 köşe varsa (bir çizgi oluşmuşsa) kapanma kontrolü yap.
            if (verticesRef.current.length >= 2) {
                const closingIndex = findClosingVertexIndex(finalPoint, verticesRef.current, 10); // 10 birim eşik değeri
                if (closingIndex !== -1) {
                    // Yakın bir köşe bulundu.
                    // 1. Son noktayı bulunan köşeye kenetle.
                    const closingVertex = verticesRef.current[closingIndex];
                    finalPoint.copy(closingVertex);

                    // 2. Kenetlenen noktayı köşe listesine ekle.
                    verticesRef.current.push(finalPoint);

                    const prevPoint = verticesRef.current.at(-2);
                    const closingDistance = prevPoint.distanceTo(finalPoint);

                    // 3. Çizim işlemini tamamla (Enter'a basılmış gibi).
                    if (closingIndex !== 0) {
                        const p2 = finalPoint.clone();
                        const uuid = THREE.MathUtils.generateUUID();
                    }

                    // Çizim işlemini tamamla
                    completeWiring();

                    // 4. handleClick fonksiyonunun geri kalanının çalışmasını engelle.
                    return;
                }
            }


            if (snapMarkerRef.current) {
                scene.remove(snapMarkerRef.current);
                snapMarkerRef.current.geometry.dispose();
                snapMarkerRef.current.material.dispose();
                snapMarkerRef.current = null;
            }

            if (snapped && snapSource) {
                const marker = createSnapMarker(8, snapSource);
                marker.position.copy(finalPoint);
                scene.add(marker);
                snapMarkerRef.current = marker;
            }

            // İlk dik kenar otomatik oluşturuldu, manuel kontrol kaldırıldı

            // İkinci tıklamada (3. nokta eklenirken) ilk kenarı ötle
            if (verticesRef.current.length === 2) {
                const firstPoint = verticesRef.current[0];
                const secondPoint = verticesRef.current[1];
                const thirdPoint = finalPoint;

                // İkinci kenar yönünü hesapla (secondPoint -> thirdPoint)
                const secondEdgeDirection = new THREE.Vector3().subVectors(thirdPoint, secondPoint).normalize();

                // İlk kenar yönü (firstPoint -> secondPoint)
                const firstEdgeDirection = new THREE.Vector3().subVectors(secondPoint, firstPoint).normalize();

                // İlk kenar yönüne dik olan vektörü hesapla
                const perpendicular = new THREE.Vector3(-firstEdgeDirection.y, firstEdgeDirection.x, 0);

                // İkinci kenar yönüne göre hangi tarafa öteleyeceğimizi belirle
                const dotProduct = secondEdgeDirection.dot(perpendicular);
                const offsetDirection = dotProduct > 0 ? perpendicular : perpendicular.clone().negate();

                // İlk kenarı 10 birim ötle ve vertices'i güncelle
                const offsetFirstPoint = firstPoint.clone().add(offsetDirection.clone().multiplyScalar(10));
                const offsetSecondPoint = secondPoint.clone().add(offsetDirection.clone().multiplyScalar(10));

                verticesRef.current[0] = offsetFirstPoint;
                verticesRef.current[1] = offsetSecondPoint;

                // Sahnedeki ilk kenar çizgisini de güncelle
                const existingLines = scene.children.filter(child =>
                    child.userData?.type === 'groundingWire' && child.userData?.isGroundingLine
                );
                existingLines.forEach(line => {
                    scene.remove(line);
                    line.geometry.dispose();
                    line.material.dispose();
                });

                // Yeni ötelenmiş ilk kenarı ekle
                const newGeometry = new THREE.BufferGeometry().setFromPoints([offsetFirstPoint, offsetSecondPoint]);
                const newMaterial = new THREE.LineBasicMaterial({ color: 0x00ff00 });
                const newLine = new THREE.Line(newGeometry, newMaterial);
                newLine.userData = {
                    type: 'groundingWire',
                    isGroundingLine: true
                };
                scene.add(newLine);
            }

            verticesRef.current.push(finalPoint);
            if (verticesRef.current.length === 1 && onWireStart) {
                onWireStart(finalPoint);
            }
            if (verticesRef.current.length > 1) {
                const prev = verticesRef.current.at(-2);
                const curr = verticesRef.current.at(-1);
            }
            if (verticesRef.current.length > 1) {
                const geometry = new THREE.BufferGeometry().setFromPoints(verticesRef.current);
                const material = new THREE.LineBasicMaterial({ color: 0xffff00 });

                if (tempLineRef.current) {
                    scene.remove(tempLineRef.current);
                    tempLineRef.current.geometry.dispose();
                    tempLineRef.current.material.dispose();
                }

                tempLineRef.current = new THREE.Line(geometry, material);
                scene.add(tempLineRef.current);
            }
            finalPointRef.current = finalPoint
        };

        const handleMouseMove = (event) => {
            if (!enabled()) return;

            const rect = domElement.getBoundingClientRect();
            const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
            const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
            lastMouse.current = { x, y };
            const mouseNDC = new THREE.Vector2(x, y);
            const worldPoint = new THREE.Vector3(x, y, 0).unproject(camera);

            // -------- Bağlantı klemensi ön izlemesi --------

            // -----------------------------------------------

            // --- senin mevcut snap/preview kodların buradan devam ediyor ---
            let dynamicSnapPoints = [...snapPoints];
            const { finalPoint, snapped, snapSource } = getSnappedPoint(
                mouseNDC,
                worldPoint,
                dynamicSnapPoints,
                camera,
                renderer,
                { snapMode: snapMode }
            );

            if (snapMarkerRef.current) {
                scene.remove(snapMarkerRef.current);
                snapMarkerRef.current.geometry.dispose();
                snapMarkerRef.current.material.dispose();
                snapMarkerRef.current = null;
            }

            if (snapped && snapSource) {
                const marker = createSnapMarker(8, snapSource);
                marker.position.copy(snapSource.position);
                scene.add(marker);
                snapMarkerRef.current = marker;
            }

            let previewPoint = finalPoint;
            if (orthoMode && verticesRef.current.length > 0) {
                const lastVertex = verticesRef.current[verticesRef.current.length - 1];
                previewPoint = getOrthoSnappedPoint(lastVertex, finalPoint);
            }

            if (verticesRef.current.length > 0) {
                let previewPoints = [...verticesRef.current, previewPoint];

                // İlk kenar ötelemesi: Eğer sadece 2 nokta varsa (otomatik ilk kenar)
                if (verticesRef.current.length === 2) {
                    const firstPoint = verticesRef.current[0];
                    const secondPoint = verticesRef.current[1];
                    const mousePoint = previewPoint;

                    // İkinci kenar yönünü hesapla (secondPoint -> mousePoint)
                    const secondEdgeDirection = new THREE.Vector3().subVectors(mousePoint, secondPoint).normalize();

                    // İlk kenar yönü (firstPoint -> secondPoint)
                    const firstEdgeDirection = new THREE.Vector3().subVectors(secondPoint, firstPoint).normalize();

                    // İlk kenar yönüne dik olan vektörü hesapla
                    const perpendicular = new THREE.Vector3(-firstEdgeDirection.y, firstEdgeDirection.x, 0);

                    // İkinci kenar yönüne göre hangi tarafa öteleyeceğimizi belirle
                    const dotProduct = secondEdgeDirection.dot(perpendicular);
                    const offsetDirection = dotProduct > 0 ? perpendicular : perpendicular.clone().negate();

                    // İlk kenarı 10 birim ötele
                    const offsetFirstPoint = firstPoint.clone().add(offsetDirection.clone().multiplyScalar(10));
                    const offsetSecondPoint = secondPoint.clone().add(offsetDirection.clone().multiplyScalar(10));

                    previewPoints = [offsetFirstPoint, offsetSecondPoint, mousePoint];

                    // ---------------------------------------------------------
                    // Ana besleme hattını bul ve MUTLAK POZİSYONLA yeniden çiz
                    let mainPanelWire = null;
                    scene.traverse(obj => {
                        if (obj.userData?.id?.startsWith("mainPanelWire")) {
                            mainPanelWire = obj;
                        }
                    });

                    if (mainPanelWire && mainPanelWire.geometry?.attributes?.position) {
                        const positions = mainPanelWire.geometry.attributes.position.array;
                        const vertexCount = positions.length / 3;

                        // Orijinal pozisyonları ilk seferde kaydet
                        if (!originalMainPanelWirePositions.current) {
                            originalMainPanelWirePositions.current = Array.from(positions);
                        }

                        const originalPositions = originalMainPanelWirePositions.current;

                        if (vertexCount >= 2) {
                            // Son iki noktayı orijinalden al
                            const lastVertexIndex = (vertexCount - 1) * 3;
                            const secondLastVertexIndex = (vertexCount - 2) * 3;

                            const lastVertex = new THREE.Vector3(
                                originalPositions[lastVertexIndex],
                                originalPositions[lastVertexIndex + 1],
                                originalPositions[lastVertexIndex + 2]
                            );
                            const secondLastVertex = new THREE.Vector3(
                                originalPositions[secondLastVertexIndex],
                                originalPositions[secondLastVertexIndex + 1],
                                originalPositions[secondLastVertexIndex + 2]
                            );

                            // Ana hattı ters yöne kaydır (her mousemove'da yeni yönle)
                            const reverseOffset = offsetDirection.clone().negate();
                            const offsetLast = lastVertex.clone().add(reverseOffset.clone().multiplyScalar(10));
                            const offsetSecondLast = secondLastVertex.clone().add(reverseOffset.clone().multiplyScalar(10));

                            // Yeni pozisyonları yaz
                            const newPositions = [...originalPositions];
                            newPositions[lastVertexIndex] = offsetLast.x;
                            newPositions[lastVertexIndex + 1] = offsetLast.y;
                            newPositions[lastVertexIndex + 2] = offsetLast.z;
                            newPositions[secondLastVertexIndex] = offsetSecondLast.x;
                            newPositions[secondLastVertexIndex + 1] = offsetSecondLast.y;
                            newPositions[secondLastVertexIndex + 2] = offsetSecondLast.z;

                            mainPanelWire.geometry.attributes.position.array = new Float32Array(newPositions);
                            mainPanelWire.geometry.attributes.position.needsUpdate = true;
                        }
                    }
                }



                const geometry = new THREE.BufferGeometry().setFromPoints(previewPoints);
                const material = new THREE.LineBasicMaterial({ color: 0xffff00 });

                if (tempLineRef.current) {
                    scene.remove(tempLineRef.current);
                    tempLineRef.current.geometry.dispose();
                    tempLineRef.current.material.dispose();
                }

                tempLineRef.current = new THREE.Line(geometry, material);
                scene.add(tempLineRef.current);
                if (previewPoint instanceof THREE.Vector3) {
                    previewEndPointRef.current = previewPoint.clone();
                }
            }

            if (previewSymbolRef.current) {
                scene.remove(previewSymbolRef.current);
                previewSymbolRef.current.traverse(child => {
                    if (child.geometry) child.geometry.dispose();
                    if (child.material) child.material.dispose();
                });
                previewSymbolRef.current = null;
            }

            if (verticesRef.current.length > 0 && previewPoint) {
                const lastPoint = verticesRef.current[verticesRef.current.length - 1];
                const directionVector = new THREE.Vector3().subVectors(previewPoint, lastPoint).normalize();
                const angle = Math.atan2(directionVector.y, directionVector.x);

                const symbolPreview = createGroundingStrake(
                    null,
                    previewPoint,
                    1, // direction için sabit
                    wireColor,
                    true,
                    socketTypeRef.current,
                    null,
                    90 + flipDir  // 90 derece + r tuşu ile eklenen açı (state kullan)
                );

                //  symbolPreview.children[0].rotation.z = angle;
                symbolPreview.rotation.z = angle;
                scene.add(symbolPreview);
                previewSymbolRef.current = symbolPreview;
            }
        };



        const addSocket = () => {
            if (!(previewEndPointRef.current instanceof THREE.Vector3)) {
                return;
            }

            verticesRef.current.push(previewEndPointRef.current.clone());

            const p1 = verticesRef.current.at(-2);
            const p2 = verticesRef.current.at(-1);
            const direction = new THREE.Vector3().subVectors(p2, p1);
            const angle = Math.atan2(direction.y, direction.x);

            // Yeni soket verisi
            const socketData = {
                position: p2.clone(),
                type: socketTypeRef.current,
                buat: null,
                direction,

            };
            socketsRef.current.push(socketData);

            // Topraklama kazığı (strake) oluşturuluyor
            const socket = createGroundingStrake(
                scene,
                p2.clone(),
                1, // direction için sabit
                wireColor,
                false, // preview değil, gerçek
                socketsRef.current,
                tempLineRef.current.uuid,
                90 + flipDir // 90 derece + r tuşu ile eklenen rotasyon (state kullan)
            );
            socket.rotation.z = angle;

            // UUID kaydet
            const uuid = socket.uuid;
            socketsRef.current[socketsRef.current.length - 1].uuid = uuid;
            strakeCount.current++
            scene.add(socket);



            if (verticesRef.current.length > 1) {
                const prev = verticesRef.current.at(-2);
                const curr = verticesRef.current.at(-1);
            }

            socketCountRef.current++;
        };

        const handleKeyDown = (e) => {
            if (!enabled()) return;
            const movementOffset = 5; // Ofset miktarı (5 birim)

            if (snapMarkerRef.current) {
                const snapPos = snapMarkerRef.current.position;
                // Önceki pozisyonu saklayalım
                const previousPos = verticesRef.current[verticesRef.current.length - 1];
                if (!previousPos) return;
                let newPos = new THREE.Vector3(previousPos.x, previousPos.y, 0); // Başlangıç noktasını alıyoruz

                // Snap'ın sağda olup olmadığını kontrol et
                const isSnapRight = (snapPos.x >= previousPos.x)
                const isSnapLeft = (snapPos.x <= previousPos.x)
                const isSnapUp = (snapPos.y >= previousPos.y)
                const isSnapDown = (snapPos.y <= previousPos.y)
                // Aşağıda, yukarıda, solda ve sağda aktif olacak tuşları kontrol et
                if (e.key === 'a' && isSnapLeft) { // Sola hareket
                    snapState.y = 0;
                    if (!snapState.x) {
                        newPos.x = snapPos.x + movementOffset;
                        snapState.x = 1;
                    } else if (snapState.x === 1) {
                        newPos.x = snapPos.x;
                        snapState.x = 2;
                    } else if (snapState.x === 2) {
                        newPos.x = snapPos.x - movementOffset;
                        snapState.x = 0;
                    }
                } else if (e.key === 'd' && isSnapRight) { // Sağa hareket
                    snapState.y = 0;
                    if (!snapState.x) {
                        newPos.x = snapPos.x - movementOffset;
                        snapState.x = 1;
                    } else if (snapState.x === 1) {
                        newPos.x = snapPos.x;
                        snapState.x = 2;
                    } else if (snapState.x === 2) {
                        newPos.x = snapPos.x + movementOffset;
                        snapState.x = 0;
                    }
                } else if (e.key === 'w' && isSnapUp) { // Yukarı hareket
                    snapState.x = 0;
                    if (!snapState.y) {
                        newPos.y = snapPos.y - movementOffset;
                        snapState.y = 1;
                    } else if (snapState.y === 1) {
                        newPos.y = snapPos.y;
                        snapState.y = 2;
                    } else if (snapState.y === 2) {
                        newPos.y = snapPos.y + movementOffset;
                        snapState.y = 0;
                    }
                } else if (e.key === 's' && isSnapDown) { // Aşağı hareket
                    snapState.x = 0;
                    if (!snapState.y) {
                        newPos.y = snapPos.y + movementOffset;
                        snapState.y = 1;
                    } else if (snapState.y === 1) {
                        newPos.y = snapPos.y;
                        snapState.y = 2;
                    } else if (snapState.y === 2) {
                        newPos.y = snapPos.y - movementOffset;
                        snapState.y = 0;
                    }
                }

                // Yeni pozisyonu ekleyelim ve geçici çizgiyi güncelleyelim
                if (newPos !== previousPos) {
                    verticesRef.current.push(newPos);
                    if (tempLineRef.current) {
                        scene.remove(tempLineRef.current);
                        tempLineRef.current.geometry.dispose();
                        tempLineRef.current.material.dispose();
                    }

                    const previewPoints = [...verticesRef.current];
                    const geometry = new THREE.BufferGeometry().setFromPoints(previewPoints);
                    const material = new THREE.LineBasicMaterial({ color: 0xffff00 });
                    tempLineRef.current = new THREE.Line(geometry, material);
                    scene.add(tempLineRef.current);
                }
            }
            if (e.key === 't') {
                socketTypeIndex.current = (socketTypeIndex.current + 1) % socketTypes.length;
                socketTypeRef.current = socketTypes[socketTypeIndex.current];
                return;
            }
            if (e.key === 'q') {
                // Mouse pozisyonunu world koordinatlarına dönüştür
                // Çizgiye ilk 5 birimi ekle
                const previousPos = verticesRef.current[verticesRef.current.length - 1];

                const direction = new THREE.Vector3(
                    previewEndPointRef.current.x - previousPos.x,
                    previewEndPointRef.current.y - previousPos.y,
                    0
                ).normalize(); // Yönü normalize et

                // Yeni pozisyonu bu yöne göre hesapla
                let newPos = new THREE.Vector3(
                    previousPos.x + direction.x * movementOffset,
                    previousPos.y + direction.y * movementOffset,
                    0
                );

                // Yeni pozisyonu ekleyelim ve geçici çizgiyi güncelleyelim
                verticesRef.current.push(newPos);
                if (tempLineRef.current) {
                    scene.remove(tempLineRef.current);
                    tempLineRef.current.geometry.dispose();
                    tempLineRef.current.material.dispose();
                }

                const previewPoints = [...verticesRef.current];
                const geometry = new THREE.BufferGeometry().setFromPoints(previewPoints);
                const material = new THREE.LineBasicMaterial({ color: 0xffff00 });
                tempLineRef.current = new THREE.Line(geometry, material);
                scene.add(tempLineRef.current);
            }
            if (e.key === 'e') {
                const previousPos = verticesRef.current[verticesRef.current.length - 1];
                if (!previousPos) return;

                // Mouse'un sahnedeki son konumunu hesapla
                const mouseNDC = new THREE.Vector2(lastMouse.current.x, lastMouse.current.y);
                const mouseWorld = new THREE.Vector3(lastMouse.current.x, lastMouse.current.y, 0).unproject(camera);

                // Mouse yönü ile son nokta arasındaki fark
                const deltaX = mouseWorld.x - previousPos.x;
                const deltaY = mouseWorld.y - previousPos.y;

                // 45 derece yönlerde ofset vektör
                const offsetLength = 7.07106781187; // toplam uzunluk
                const dx = offsetLength / Math.sqrt(2); // 3.5355
                const dy = dx;

                let offset = new THREE.Vector3();

                if (deltaX >= 0 && deltaY >= 0) {
                    // Sağ üst (45°)
                    offset.set(dx, dy, 0);
                } else if (deltaX < 0 && deltaY >= 0) {
                    // Sol üst (135°)
                    offset.set(-dx, dy, 0);
                } else if (deltaX < 0 && deltaY < 0) {
                    // Sol alt (225°)
                    offset.set(-dx, -dy, 0);
                } else {
                    // Sağ alt (315°)
                    offset.set(dx, -dy, 0);
                }

                const newPos = new THREE.Vector3().addVectors(previousPos, offset);
                verticesRef.current.push(newPos);

                if (tempLineRef.current) {
                    scene.remove(tempLineRef.current);
                    tempLineRef.current.geometry.dispose();
                    tempLineRef.current.material.dispose();
                }

                const previewPoints = [...verticesRef.current];
                const geometry = new THREE.BufferGeometry().setFromPoints(previewPoints);
                const material = new THREE.LineBasicMaterial({ color: 0xffff00 });
                tempLineRef.current = new THREE.Line(geometry, material);
                scene.add(tempLineRef.current);
            }
            const cancelWiring = () => {
                // Seçili groundingStrake'leri eski rengine döndür
                scene.traverse((child) => {
                    if (child.userData?.type === 'groundingStrake' && child.userData?.isSelected) {
                        child.userData.isSelected = false;
                        child.traverse((obj) => {
                            if (obj.isMesh || obj.type === 'Line') {
                                if (obj.material && obj.material.color) {
                                    const orig = obj.userData?.originalColor;
                                    if (orig !== undefined) {
                                        obj.material.color.set(orig);
                                    }
                                }
                            }
                        });
                    }
                });

                // MainPanel wire'ı orijinal pozisyonuna döndür
                if (originalMainPanelWirePositions.current) {
                    let mainPanelWire = null;
                    scene.traverse(obj => {
                        if (obj.userData?.id?.startsWith("mainPanelWire")) {
                            mainPanelWire = obj;
                        }
                    });

                    if (mainPanelWire && mainPanelWire.geometry?.attributes?.position) {
                        mainPanelWire.geometry.attributes.position.array = new Float32Array(originalMainPanelWirePositions.current);
                        mainPanelWire.geometry.attributes.position.needsUpdate = true;
                    }
                    originalMainPanelWirePositions.current = null;
                }

                // Grounding wire çizgilerini temizle
                const groundingWiresToRemove = [];
                scene.traverse((child) => {
                    if (child.userData?.type === 'groundingWire' && child.userData?.isGroundingLine) {
                        groundingWiresToRemove.push(child);
                    }
                });
                groundingWiresToRemove.forEach(line => {
                    scene.remove(line);
                    line.geometry.dispose();
                    line.material.dispose();
                });

                // Geçici çizgiyi temizle
                if (tempLineRef.current) {
                    scene.remove(tempLineRef.current);
                    tempLineRef.current.geometry.dispose();
                    tempLineRef.current.material.dispose();
                    tempLineRef.current = null;
                }

                // Preview symbol temizle
                if (previewSymbolRef.current) {
                    scene.remove(previewSymbolRef.current);
                    previewSymbolRef.current.traverse((child) => {
                        if (child.geometry) child.geometry.dispose();
                        if (child.material) child.material.dispose();
                    });
                    previewSymbolRef.current = null;
                }

                // Ön izleme sembollerini temizle
                const toRemove = [];
                scene.traverse((child) => {
                    if (
                        (child.userData?.type === 'groundingStrake' && child.userData?.isPreview) ||
                        child.userData?.type === 'buatCircle' ||
                        child.userData?.type === 'groundingBuatLabel' ||
                        child.userData?.type === 'groundingLabel'
                    ) {
                        toRemove.push(child);
                    }
                });
                toRemove.forEach((child) => {
                    if (child.traverse && typeof child.traverse === 'function') {
                        child.traverse((obj) => {
                            if (obj.geometry) obj.geometry.dispose();
                            if (obj.material) obj.material.dispose();
                        });
                    }
                    scene.remove(child);
                });

                // Snap marker'ı temizle
                if (snapMarkerRef.current) {
                    scene.remove(snapMarkerRef.current);
                    snapMarkerRef.current.geometry.dispose();
                    snapMarkerRef.current.material.dispose();
                    snapMarkerRef.current = null;
                }

                // Ref ve state sıfırlama
                verticesRef.current = [];
                socketsRef.current = [];
                previewMarkersRef.current = [];
                previewSymbolRef.current = null;
                offsetLineRef.current = null;
                intersectedLineRef.current = null;
                snapSourceRef.current = null;
                finalPointRef.current = null;
                previewEndPointRef.current = { x: 0, y: 0 };
                originalMainPanelWirePositions.current = null;
                mainPanelWireOffsetApplied.current = false;

                startRef.current = false;
                setStep(0);

                // 🔑 Callback'i çağır ve state'i resetle
                onWireCancel(); // Bu useMPanelGrounding'deki wiringActiveRef.current = false yapacak
                dispatch(setCommandMessage(""));
            };





            if (e.key === 'Escape' || e.key === 'Esc' || e.code === 'Escape') {
                if (!enabled()) return; // Eğer hook aktif değilse ESC etkisiz

                cancelWiring();
                return;
            }

            if (e.key === ' ') {
                addSocket()
            }
            if (e.key === 'Enter') {
                setStep(0)

                // Preview'daki ötelenmiş vertices'i kalıcı vertices'e kaydet
                if (verticesRef.current.length === 2 && previewEndPointRef.current) {
                    const firstPoint = verticesRef.current[0];
                    const secondPoint = verticesRef.current[1];
                    const thirdPoint = previewEndPointRef.current;

                    // İkinci kenar yönünü hesapla (secondPoint -> thirdPoint)
                    const secondEdgeDirection = new THREE.Vector3().subVectors(thirdPoint, secondPoint).normalize();

                    // İlk kenar yönü (firstPoint -> secondPoint)
                    const firstEdgeDirection = new THREE.Vector3().subVectors(secondPoint, firstPoint).normalize();

                    // İlk kenar yönüne dik olan vektörü hesapla
                    const perpendicular = new THREE.Vector3(-firstEdgeDirection.y, firstEdgeDirection.x, 0);

                    // İkinci kenar yönüne göre hangi tarafa öteleyeceğimizi belirle
                    const dotProduct = secondEdgeDirection.dot(perpendicular);
                    const offsetDirection = dotProduct > 0 ? perpendicular : perpendicular.clone().negate();

                    // İlk kenarı 10 birim ötle ve vertices'i güncelle
                    const offsetFirstPoint = firstPoint.clone().add(offsetDirection.clone().multiplyScalar(10));
                    const offsetSecondPoint = secondPoint.clone().add(offsetDirection.clone().multiplyScalar(10));

                    verticesRef.current[0] = offsetFirstPoint;
                    verticesRef.current[1] = offsetSecondPoint;
                    verticesRef.current.push(thirdPoint);

                    // Sahnedeki eski ilk kenar çizgisini güncelle
                    const existingLines = scene.children.filter(child =>
                        child.userData?.type === 'groundingWire' && child.userData?.isGroundingLine
                    );
                    existingLines.forEach(line => {
                        scene.remove(line);
                        line.geometry.dispose();
                        line.material.dispose();
                    });

                    // Yeni ötelenmiş ilk kenarı ekle
                    const newGeometry = new THREE.BufferGeometry().setFromPoints([offsetFirstPoint, offsetSecondPoint]);
                    const newMaterial = new THREE.LineBasicMaterial({ color: 0x00ff00 });
                    const newLine = new THREE.Line(newGeometry, newMaterial);
                    newLine.userData = {
                        type: 'groundingWire',
                        isGroundingLine: true
                    };
                    scene.add(newLine);
                } else if (previewEndPointRef.current) {
                    // Normal durum: son noktayı ekle
                    verticesRef.current.push(previewEndPointRef.current);
                }

                completeWiring();
                if (previewEndPointRef.current && verticesRef.current.length > 1) {
                    // Kalıcı çizgiyi oluştur
                    const geometry = new THREE.BufferGeometry().setFromPoints(verticesRef.current);
                    const material = new THREE.LineBasicMaterial({ color: wireColor }); // Kalıcı çizgi rengi
                    const permanentLine = new THREE.Line(geometry, material);

                    // UUID'sini eski çizgiden kopyala (takip kolaylığı için)
                    permanentLine.uuid = tempLineRef.current?.uuid ?? THREE.MathUtils.generateUUID();
                    permanentLine.userData = {
                        type: 'groundingStrip',
                        sockets: socketsRef.current.map(s => s.uuid),
                    };

                    scene.add(permanentLine);

                    // onWireEnd'e bildir
                    onWireEnd(verticesRef.current, socketsRef.current, snapSourceRef.current, permanentLine.uuid);
                }


                if (previewSymbolRef.current) {
                    scene.remove(previewSymbolRef.current);
                    previewSymbolRef.current.traverse((child) => {
                        if (child.geometry) child.geometry.dispose();
                        if (child.material) child.material.dispose();
                    });
                    previewSymbolRef.current = null;
                }
                socketsRef.current = [];
                verticesRef.current = [];
                startRef.current = false;

                if (tempLineRef.current) {
                    scene.remove(tempLineRef.current);
                    tempLineRef.current.geometry.dispose();
                    tempLineRef.current.material.dispose();
                    tempLineRef.current = null;
                }
            }

            if (e.key === 'r') {
                setFlipDirection(prev => {
                    const newRotation = (prev + 90) % 360;
                    // aktif önizleme varsa, offsetGroup'u güncelle
                    if (previewSymbolRef.current) {
                        const offset = previewSymbolRef.current.children?.[0];
                        if (offset) {
                            offset.rotation.z = THREE.MathUtils.degToRad(90 + newRotation);
                        }
                    }
                    return newRotation;
                });
                return;
            }


        };

        const throttledMouseMove = throttleRAF(handleMouseMove);

        domElement.addEventListener('click', handleClick);
        domElement.addEventListener('mousemove', throttledMouseMove);
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            throttledMouseMove.cancel();
            domElement.removeEventListener('click', handleClick);
            domElement.removeEventListener('mousemove', throttledMouseMove);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [commandType, scene, camera, renderer, gridMode, objectSnap, flipDir, snapPoints, enabled]);
};

export default useMPanelGroundWiring;
